/**
 * Axios API Client Configuration
 * Centralized HTTP client with interceptors for authentication
 */

import axios from 'axios';
import { getAccessToken, getRefreshToken, isAccessTokenExpired, setAuthData, deleteAuthData, isLogoutInProgress } from '../auth/authUtils';
import { ENDPOINTS } from './endpoints';
import { TIMEOUTS } from '../constants';
import { isNativePlatform, getPlatform } from '../native';
import { getCurrentLanguage } from '../i18n';
import { withRequestCache } from '../cache/requestCache';

/**
 * Tells the server this request came from the installed app rather than a
 * browser. The Capacitor WebView sends an ordinary Chrome-on-Android
 * User-Agent, so without this the session list would label the app a mobile
 * browser. Safe to import: the native module has no top-level Capacitor deps.
 */
const CLIENT_PLATFORM_HEADER = 'X-Client-Platform';
const clientPlatform = isNativePlatform() ? `${getPlatform()}-app` : null;

const parseRetryAfterSeconds = (retryAfterHeader) => {
  if (!retryAfterHeader) {
    return null;
  }

  const normalized = String(retryAfterHeader).trim();
  if (!normalized) {
    return null;
  }

  const deltaSeconds = Number.parseInt(normalized, 10);
  if (!Number.isNaN(deltaSeconds)) {
    return Math.max(deltaSeconds, 1);
  }

  const retryDateMs = Date.parse(normalized);
  if (Number.isNaN(retryDateMs)) {
    return null;
  }

  const secondsUntilRetry = Math.ceil((retryDateMs - Date.now()) / 1000);
  return Math.max(secondsUntilRetry, 1);
};

const parseRetryAfterFromError = (error) => {
  const headerValue = error.response?.headers?.['retry-after'];
  const headerSeconds = parseRetryAfterSeconds(headerValue);
  if (headerSeconds) {
    return headerSeconds;
  }

  const bodyValue = Number(error.response?.data?.retryAfterSeconds);
  if (Number.isFinite(bodyValue) && bodyValue > 0) {
    return Math.ceil(bodyValue);
  }

  return null;
};

// Create axios instance with default configuration. Caching is the server's
// call (files carry an ETag and a long max-age, API JSON is never cached), so
// no request-side Cache-Control; GETs may opt into the in-memory request
// cache with `{ cache: { ttl } }` — see core/cache/requestCache.js.
const apiClient = withRequestCache(axios.create({
  baseURL: process.env.REACT_APP_URL,
  timeout: TIMEOUTS.DEFAULT,
  headers: {
    Accept: '*/*',
    ...(clientPlatform ? { [CLIENT_PLATFORM_HEADER]: clientPlatform } : {}),
  },
}));

/**
 * Refresh the access token using the refresh token
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  return axios.post(
    process.env.REACT_APP_URL + ENDPOINTS.AUTH.REFRESH,
    { refreshToken }
  );
};

/**
 * Request interceptor
 * - Adds authorization header
 * - Handles token refresh if expired
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Resolve server-side dynamic content (course names, announcements) into the
    // language the user picked in-app.
    config.headers['Accept-Language'] = getCurrentLanguage();

    let token = getAccessToken();

    // Check if token is expired and refresh is available
    if (token && isAccessTokenExpired() && getRefreshToken()) {
      try {
        const refreshResponse = await refreshAccessToken();
        if (refreshResponse?.data?.success) {
          setAuthData(refreshResponse.data.data);
          token = getAccessToken();
        } else {
          deleteAuthData();
          window.location.href = '/login';
          return Promise.reject(new Error('Refresh token invalid'));
        }
      } catch (error) {
        deleteAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Add authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor
 * - Handles errors globally
 * - Redirects to login on 401
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Suppress all errors during logout
    if (isLogoutInProgress()) {
      return Promise.reject(error);
    }

    const backendMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      'An unexpected error occurred';

    const statusCode = error.response?.status || null;
    const retryAfterSeconds = parseRetryAfterFromError(error);

    // Attach readable message for easier error handling
    error.readableMessage = backendMessage;
    error.statusCode = statusCode;
    error.retryAfterSeconds = retryAfterSeconds;
    error.isRateLimited = statusCode === 429;

    // Handle unauthorized errors
    if (statusCode === 401) {
      deleteAuthData();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export default apiClient;

